/**
 * PropertyForestMap — shared base component for all Leaflet map surfaces.
 *
 * Fixes the sizing bug caused by using `height: '70vh'`
 * on the outer div, which cannot size a MapContainer with `height: 100%` when
 * the parent has no explicit height. This base ALWAYS accepts a pixel height
 * and owns the outer div sizing.
 *
 * The caller owns server-state loading and passes an immutable GeoJSON/bbox
 * snapshot. This renderer never imports query hooks or API clients.
 *
 * Requirements: FLOW-PLAN-UI-09, FLOW-PLAN-UI-10 (map sizing fix)
 */
import React, { useEffect, useMemo, type ReactNode } from 'react';
import { MapContainer as LeafletMapContainer, useMap } from 'react-leaflet';
import type * as GeoJSONTypes from 'geojson';
import { useMapLocalization } from '@/hooks/useMapLocalization';
import { FmMmlBasemapTiles } from '@/shared/ui/FmStandBasemapTiles';
import { MapFitController } from '@/components/Map/MapFitController';
import { resolveMapFitBounds, type MapFitTarget } from '@/components/Map/mapFitTarget';

// Types

export interface PropertyForestMapState {
  standsGeoJson: GeoJSONTypes.FeatureCollection | null;
  propertyBoundaryGeoJson: GeoJSONTypes.GeoJsonObject | null;
  bbox: [number, number, number, number] | null;
  isLoading: boolean;
  error: string | null;
}

export interface PropertyForestMapProps {
  /**
   * Explicit pixel height for the map container. NEVER use vh or % here.
   * This is the literal fix for the map sizing bug.
   * Can be omitted if size variant is provided.
   */
  height?: number;

  /**
   * Caller-supplied GeoJSON from a domain query snapshot.
   */
  geojson?: GeoJSONTypes.FeatureCollection | null;
  /**
   * Explicit mode: caller-supplied bbox [minLng, minLat, maxLng, maxLat].
   */
  bbox?: [number, number, number, number] | null;
  /**
   * Controls automatic property framing. Embedded review maps fit once for
   * the supplied identity and thereafter preserve deliberate viewport edits.
   */
  fitBoundsMode?: 'bounds-change' | 'initial';
  fitBoundsIdentity?: string;
  /** Frame these features instead of the property bbox (`presentation.fitTo`). */
  fitFeatureIds?: readonly string[];
  /** Deepest zoom a fit may reach (the basemap's native coverage). */
  fitMaxZoom?: number;

  // Map configuration

  /** Default map center before fitBounds runs. Defaults to Finland center. */
  defaultCenter?: [number, number];
  /** Default zoom level. Defaults to 12. */
  defaultZoom?: number;
  /**
   * Whether to render the default basemap (MML maastokartta with OSM fallback).
   * Set to 'none' when the wrapper manages its own LayersControl with tiles.
   * Defaults to 'base'.
   */
  tileLayer?: 'base' | 'none';
  /** Additional map options forwarded to MapContainer. */
  mapOptions?: {
    scrollWheelZoom?: boolean;
    doubleClickZoom?: boolean;
    dragging?: boolean;
    zoomControl?: boolean;
    minZoom?: number;
    maxZoom?: number;
    zoomSnap?: number;
    /** Disable zoom/fade/marker animations for deterministic, jank-free renders. */
    zoomAnimation?: boolean;
    fadeAnimation?: boolean;
    markerZoomAnimation?: boolean;
  };
  /** Whether to render a 1px solid border. Defaults to true. */
  border?: boolean;
  /** Border radius in pixels. Defaults to 8. */
  borderRadius?: number;

  /** Named size variant for height: 'preview', 'compact', 'tall'.
   * Only used if height is not explicitly provided.
   * Allows reusable sizing patterns across the app. */
  size?: 'preview' | 'compact' | 'tall';

  // Render content

  /**
   * Children rendered INSIDE the LeafletMapContainer (e.g. GeoJSON layers,
   * LayersControl, Pane definitions). All react-leaflet children must be
   * inside a MapContainer — pass them here.
   */
  children?: ReactNode;

  /**
   * Render-prop variant. Called with the resolved map state (geojson, bbox,
   * isLoading, error). The returned nodes are rendered inside the
   * LeafletMapContainer. Useful when the caller needs the resolved data to
   * construct its layers. Use either `children` OR `mapChildren`, not both.
   */
  mapChildren?: (state: PropertyForestMapState) => ReactNode;

  /**
   * Overlay nodes rendered as siblings OUTSIDE the LeafletMapContainer but
   * INSIDE the outer `position: relative` wrapper. Use for absolutely-
   * positioned overlays such as map legends.
   */
  overlays?: ReactNode;

  /**
   * data-testid applied to the outermost element. Each wrapper sets its own
   * to preserve per-surface test IDs.
   */
  testId?: string;
}

// Inner components: the deterministic fit and the size invalidation

interface PropertyFitTargetInput {
  bbox: [number, number, number, number] | null;
  geojson: GeoJSONTypes.FeatureCollection | null;
  featureIds: readonly string[] | undefined;
  mode: 'bounds-change' | 'initial';
  identity: string;
  maxZoom: number | undefined;
}

/**
 * One fit target per map. `initial` keeps the target for the map's identity
 * (deliberate viewport edits survive data refreshes); `bounds-change` takes a
 * new target whenever the bounds change. The basis (named features or the
 * property bbox) is part of the identity, so a frame that waited for the
 * stand geometry fits once that geometry arrives.
 */
function usePropertyFitTarget(input: PropertyFitTargetInput): MapFitTarget | null {
  const { bbox, geojson, featureIds, mode, identity, maxZoom } = input;
  return useMemo(() => {
    const bounds = resolveMapFitBounds(bbox, geojson, featureIds);
    if (!bounds) return null;
    const basis = featureIds?.length && geojson ? `features:${[...featureIds].sort().join(',')}` : 'bbox';
    const boundsKey = bounds.toBBoxString();
    return {
      key: `${mode === 'initial' ? identity : boundsKey}|${basis}`,
      bounds,
      maxZoom,
    };
  }, [bbox, featureIds, geojson, identity, maxZoom, mode]);
}

/** Leaflet caches its container size; every container resize invalidates it. */
const MapSizeInvalidator: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => map.invalidateSize(false));
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);

  return null;
};

const LocalizedMapControls: React.FC = () => {
  useMapLocalization();
  return null;
};

// Basemap default — MML maastokartta via the api-key-injecting proxy, with
// built-in OSM fallback (direct OSM primary tiles violate OSMF usage policy).

const BASE_TILE_LAYER = <FmMmlBasemapTiles layer="maastokartta" />;

// Default map center — center of Finland. This is a UI default, not a
// calculation fallback value.
const FINLAND_CENTER: [number, number] = [64.2008, 27.6267];

// Named size presets for PropertyForestMap — aligns with FmMapSnapshot sizes
const HEIGHT_PRESETS: Record<'preview' | 'compact' | 'tall', number> = {
  preview: 500, // Hero map preview variant
  compact: 300, // Sidebar miniature maps
  tall: 600, // Full-workspace report displays
} as const;

type MapOptions = NonNullable<PropertyForestMapProps['mapOptions']>;

/** Resolve the map's immutable caller-owned data snapshot. */
function resolveMapState(
  geojson: GeoJSONTypes.FeatureCollection | null | undefined,
  explicitBbox: [number, number, number, number] | null | undefined,
): PropertyForestMapState {
  return {
    standsGeoJson: geojson ?? null,
    propertyBoundaryGeoJson: null,
    bbox: explicitBbox ?? null,
    isLoading: false,
    error: null,
  };
}

/** Build the outer wrapper style (explicit pixel height — never vh/%). */
function buildOuterStyle(
  resolvedHeight: number,
  borderRadius: number,
  border: boolean,
  preservePageScroll: boolean,
): React.CSSProperties {
  return {
    position: 'relative',
    // CRITICAL: use explicit pixel height (never vh or %) — via height prop or size variant.
    // A vh or % height collapses the Leaflet container.
    height: `${resolvedHeight}px`,
    width: '100%',
    borderRadius: `${borderRadius}px`,
    border: border ? '1px solid var(--fm-d-border)' : undefined,
    overflow: 'hidden',
    touchAction: preservePageScroll ? 'pan-y' : undefined,
  };
}

interface ResolvedMapOptions {
  scrollWheelZoom: boolean;
  doubleClickZoom: boolean;
  dragging: boolean;
  zoomControl: boolean;
  minZoom: number | undefined;
  maxZoom: number | undefined;
  zoomSnap: number | undefined;
  zoomAnimation: boolean | undefined;
  fadeAnimation: boolean | undefined;
  markerZoomAnimation: boolean | undefined;
}

/** Apply MapContainer option defaults once, outside the render JSX. */
function resolveMapOptions(mapOptions: MapOptions): ResolvedMapOptions {
  return {
    scrollWheelZoom: mapOptions.scrollWheelZoom ?? true,
    doubleClickZoom: mapOptions.doubleClickZoom ?? true,
    dragging: mapOptions.dragging ?? true,
    zoomControl: mapOptions.zoomControl ?? true,
    minZoom: mapOptions.minZoom,
    maxZoom: mapOptions.maxZoom,
    zoomSnap: mapOptions.zoomSnap,
    // Raster-backed workspaces favor continuity over Leaflet's zoom/fade
    // effects. During a rapid trackpad gesture those effects can discard the
    // previous tile grid before the replacement images arrive, exposing the
    // dark map canvas. Callers may opt back in for a bounded preview.
    zoomAnimation: mapOptions.zoomAnimation ?? false,
    fadeAnimation: mapOptions.fadeAnimation ?? false,
    markerZoomAnimation: mapOptions.markerZoomAnimation ?? false,
  };
}

/** Resolve all prop defaults in one place so the component body stays simple. */
function resolvePropsWithDefaults(props: PropertyForestMapProps) {
  return {
    height: props.height,
    geojson: props.geojson,
    explicitBbox: props.bbox,
    fitBoundsMode: props.fitBoundsMode ?? 'bounds-change',
    fitBoundsIdentity: props.fitBoundsIdentity,
    fitFeatureIds: props.fitFeatureIds,
    fitMaxZoom: props.fitMaxZoom,
    defaultCenter: props.defaultCenter ?? FINLAND_CENTER,
    defaultZoom: props.defaultZoom ?? 12,
    tileLayer: props.tileLayer ?? 'base',
    mapOptions: props.mapOptions ?? {},
    border: props.border ?? true,
    borderRadius: props.borderRadius ?? 8,
    children: props.children,
    mapChildren: props.mapChildren,
    overlays: props.overlays,
    testId: props.testId,
    size: props.size,
  };
}

// Main component

const PropertyForestMap: React.FC<PropertyForestMapProps> = (props) => {
  const {
    height,
    geojson,
    explicitBbox,
    fitBoundsMode,
    fitBoundsIdentity,
    fitFeatureIds,
    fitMaxZoom,
    defaultCenter,
    defaultZoom,
    tileLayer,
    mapOptions,
    border,
    borderRadius,
    children,
    mapChildren,
    overlays,
    testId,
    size,
  } = resolvePropsWithDefaults(props);

  const mapState = resolveMapState(geojson, explicitBbox);
  const resolvedFitIdentity =
    fitBoundsIdentity ?? mapState.bbox?.join(':') ?? 'unavailable-bounds';
  const fitTarget = usePropertyFitTarget({
    bbox: mapState.bbox,
    geojson: mapState.standsGeoJson,
    featureIds: fitFeatureIds,
    mode: fitBoundsMode,
    identity: resolvedFitIdentity,
    maxZoom: fitMaxZoom,
  });

  // Resolve height from explicit value, size variant, or fallback (400px default)
  const resolvedHeight = height ?? (size ? HEIGHT_PRESETS[size] : 400);

  const resolvedMapOptions = resolveMapOptions(mapOptions);
  const preservePageScroll = !resolvedMapOptions.scrollWheelZoom;
  const outerStyle = buildOuterStyle(
    resolvedHeight,
    borderRadius,
    border,
    preservePageScroll,
  );

  // Map render

  const resolvedChildren = mapChildren ? mapChildren(mapState) : children;

  return (
    <div
      data-testid={testId}
      style={outerStyle}
      onWheelCapture={(event) => {
        if (!resolvedMapOptions.scrollWheelZoom) {
          event.nativeEvent.stopImmediatePropagation();
        }
      }}
    >
      <LeafletMapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{
          height: '100%',
          width: '100%',
          touchAction: preservePageScroll ? 'pan-y' : undefined,
        }}
        scrollWheelZoom={resolvedMapOptions.scrollWheelZoom}
        doubleClickZoom={resolvedMapOptions.doubleClickZoom}
        dragging={resolvedMapOptions.dragging}
        zoomControl={resolvedMapOptions.zoomControl}
        minZoom={resolvedMapOptions.minZoom}
        maxZoom={resolvedMapOptions.maxZoom}
        zoomSnap={resolvedMapOptions.zoomSnap}
        zoomAnimation={resolvedMapOptions.zoomAnimation}
        fadeAnimation={resolvedMapOptions.fadeAnimation}
        markerZoomAnimation={resolvedMapOptions.markerZoomAnimation}
      >
        <LocalizedMapControls />
        <MapSizeInvalidator />
        <MapFitController target={fitTarget} />

        {tileLayer === 'base' && BASE_TILE_LAYER}

        {resolvedChildren}
      </LeafletMapContainer>

      {overlays}
    </div>
  );
};

export default PropertyForestMap;
